import React, { useRef, useEffect, useState } from 'react';
import './BuyPassModal.css';
import { Link } from 'react-router-dom';
import PurchaseResultModal from '../PurchaseResultModal/PurchaseResultModal';

import StepOneConfirmQuantity from './StepOneConfirmQuantity';
import StepTwoRecurringOption from './StepTwoRecurringOption';
import StepThreeCheckout from './StepThreeCheckout';
import { getTitleString } from './namingHelpers';

import { loadStripe } from '@stripe/stripe-js';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function BuyPassModal({
  isOpen,
  onClose,
  selectedOption,
  user,
  isAuthenticated,
  useSavedCard,
  setUseSavedCard,
  onPaymentSuccess,
  onPaymentFailure,
  productMapping,
}) {
  const overlayRef = useRef(null);

  // Track pass quantity
  const [quantity, setQuantity] = useState(1);

  // Track current step in the modal flow (1, 2, or 3)
  const [currentStep, setCurrentStep] = useState(1);

  // Track whether the purchase was successful or failed
  const [purchaseResult, setPurchaseResult] = useState(null);

  // Final price (including tax) returned from the server upon success
  const [finalPrice, setFinalPrice] = useState('');

  // Whether the pass(es) should automatically renew every month
  const [makeRecurring, setMakeRecurring] = useState(false);

  // Check if the selected pass option is a monthly pass
  const isMonthly = (selectedOption === 'Monthly Pass');

  const handlePaymentSuccess = (price) => {
    setFinalPrice(price);
    setPurchaseResult('success');
  };

  const handlePaymentFailure = () => {
    setPurchaseResult('fail');
  };

  // Reset modal when closing
  const handleClose = () => {
    setCurrentStep(1);
    setQuantity(1);
    setMakeRecurring(false);
    onClose();
  };

  const handleCloseResultModal = () => {
    setPurchaseResult(null);
    handleClose();
  };

  // Close modal if user clicks outside it
  useEffect(() => {
    function handleClickOutside(event) {
      if (overlayRef.current && overlayRef.current === event.target) {
        handleClose();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Step logic
  let stepContent;
  if (currentStep === 1) {
    // STEP 1: Confirm quantity
    stepContent = (
      <StepOneConfirmQuantity
        defaultQuantity={quantity}
        onQuantityConfirmed={(pickedQty) => setQuantity(pickedQty)}
        onContinue={() => {
          if (isMonthly) {
            setCurrentStep(2);
          } else {
            setCurrentStep(3);
          }
        }}
      />
    );
  } else if (currentStep === 2 && isMonthly) {
    // STEP 2: Choose recurring or not (all passes or none)
    stepContent = (
      <StepTwoRecurringOption
        quantity={quantity}
        onSetMakeRecurring={(boolVal) => setMakeRecurring(boolVal)}
        onBack={() => setCurrentStep(1)}
        onContinue={() => setCurrentStep(3)}
      />
    );
  } else {
    // STEP 3: Final checkout
    stepContent = (
      <StepThreeCheckout
        stripePromise={stripePromise}
        user={user}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentFailure={handlePaymentFailure}
        onBack={() => {
          if (isMonthly) setCurrentStep(2);
          else setCurrentStep(1);
        }}
        quantity={quantity}
        selectedOption={selectedOption}
        useSavedCard={useSavedCard}
        setUseSavedCard={setUseSavedCard}
        makeRecurring={makeRecurring}
        productMapping={productMapping}
      />
    );
  }

  // Do not render if the modal is closed
  if (!isOpen) return null;

  // Title of the modal based on the selected pass and quantity
  const modalTitle = getTitleString(selectedOption, quantity);

  return (
    <>
      <div className="buy-pass-modal-overlay" ref={overlayRef}>
        <div className="buy-pass-modal-content">
          <button className="buy-pass-modal-close-button" onClick={handleClose}>×</button>
          <h2 className="buy-pass-modal-title">{modalTitle}</h2>

          {!isAuthenticated ? (
            <div className="buy-pass-modal-auth-prompt">
              <p>You need to be logged in to make a purchase.</p>
              <p>
                <Link to="/login" className="buy-pass-modal-auth-link">Log In</Link> or{" "}
                <Link to="/New-Account" className="buy-pass-modal-auth-link">Sign Up</Link>
              </p>
            </div>
          ) : (
            stepContent
          )}
        </div>
      </div>

      <PurchaseResultModal
        isOpen={purchaseResult !== null}
        onClose={handleCloseResultModal}
        result={purchaseResult}
        selectedOption={selectedOption}
        quantity={quantity}
        price={finalPrice}
      />
    </>
  );
}

export default BuyPassModal;
